import React, { useState, useRef } from 'react';

import { Upload, Download, CheckCircle, AlertCircle } from "lucide-react";
import Button from '../Buttons/Button';

interface InviteMember {
  name: string;
  email: string;
  functionIds: string[];
  groupName: string;
}

interface Function {
  id: string;
  name: string;
  description?: string;
}

interface Group {
  id: string;
  name: string;
}

interface BulkUploadProps {
  onUpload: (csvData: InviteMember[]) => void;
  isSubmitting: boolean;
  functions: Function[];
  groups: Group[];
  tenantId: string;
}

const BulkUpload = ({ functions, groups, tenantId, isSubmitting, onUpload }: BulkUploadProps) => {
  const [csvData, setCsvData] = useState<InviteMember[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isValid, setIsValid] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 250 * 1024 * 1024) {
      setErrors(['File size exceeds 250 MB limit']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCsv(text);
    };
    reader.readAsText(file);
  };

  const parseCsv = (csvText: string) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const validationErrors: string[] = [];
    const parsedData: InviteMember[] = [];
    const seenEmails = new Set<string>();

    // Skip comment lines and header
    const dataLines = lines.filter(line => !line.startsWith('#'));
    const headerLine = dataLines[0];
    const dataRows = dataLines.slice(1);

    dataRows.forEach((line, index) => {
      // Split by comma, but allow for extra columns and always map by index
      const fields = line.split(',');
      const email = fields[0]?.trim();
      const fullName = fields[1]?.trim();
      const groupName = fields[2]?.trim();
      // Join any extra columns after the 3rd as functionNames (handles extra commas)
      const functionNames = fields.slice(3).join(',').trim();

      if (!fullName || !email) {
        validationErrors.push(`Row ${index + 2}: Missing name or email`);
        return;
      }

      if (!validateEmail(email)) {
        validationErrors.push(`Row ${index + 2}: Invalid email format`);
        return;
      }

      if (seenEmails.has(email.toLowerCase())) {
        validationErrors.push(`Row ${index + 2}: Duplicate email "${email}"`);
        return;
      }
      seenEmails.add(email.toLowerCase());

      if (!groupName) {
        validationErrors.push(`Row ${index + 2}: Missing group name`);
        return;
      }

      // Validate group exists
      const group = groups.find(g => g.name.trim().toLowerCase() === groupName.trim().toLowerCase());
      if (!group) {
        validationErrors.push(`Row ${index + 2}: Unknown group "${groupName}"`);
        return;
      }

      const functionIds: string[] = [];
      if (functionNames && functionNames.trim()) {
        const functionNameList = functionNames.split(',').map(fn => fn.trim()).filter(Boolean);
        for (const fnName of functionNameList) {
          const matched = functions.find(f => f.name.toLowerCase() === fnName.toLowerCase());
          if (!matched) {
            validationErrors.push(`Row ${index + 2}: Unknown function "${fnName}"`);
          } else if (!functionIds.includes(matched.id)) {
            functionIds.push(matched.id);
          }
        }
      }

      if (functionIds.length === 0) {
        validationErrors.push(`Row ${index + 2}: No valid functions assigned`);
        return;
      }

      parsedData.push({ 
        name: fullName, 
        email, 
        functionIds,
        groupName: group.name
      });
    });

    setCsvData(parsedData);
    setErrors(validationErrors);
    setIsValid(validationErrors.length === 0 && parsedData.length > 0);
  };

  const handleSubmit = async () => {
    if (!isValid || csvData.length === 0) return;
    try {
      onUpload(csvData); // removed unnecessary await
      setCsvData([]);
      setErrors([]);
      setIsValid(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setErrors([`Upload failed: ${err?.message || 'Unknown error'}`]);
    }
  };

  const downloadTemplate = () => {
    // Build dynamic lists
    const groupNames = groups.map(g => g.name).join(', ');
    const functionNames = functions.map((f: any) => f.name).join(', ');
    const instructions = [
      '# Please fill in the following columns.',
      '# Use the exact group and function names as shown below.',
      '# Multiple function names should be separated by commas.',
      `# Available group names: ${groupNames}`,
      `# Available function names: ${functionNames}`,
    ];
    const headers = ['email', 'fullName', 'groupName', 'functionNames'];
    const sampleData = [
      ['john.doe@example.com', 'John Doe', groups[0]?.name || '', functions.slice(0,2).map((f: any) => f.name).join(', ')],
      ['jane.smith@example.com', 'Jane Smith', groups[1]?.name || '', functions[0]?.name || ''],
    ];
    
    const csvContent = [
      ...instructions,
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team_invitations_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getFunctionNames = (functionIds: string[]) => {
    return functionIds.map(id => functions.find(f => f.id === id)?.name || id);
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="h5 fw-bold text-dark">Upload CSV File</h3>
        <Button
          type="button"
          variant="outline"
          onClick={downloadTemplate}
          className="btn btn-outline-success"
        >
          <Download style={{ width: 18, height: 18, marginRight: 8 }} />
          Download Template
        </Button>
      </div>
      <div className="border border-2 border-secondary rounded p-4 text-center mb-3" style={{ borderStyle: 'dashed' }}>
        <Upload style={{ width: 48, height: 48, color: '#adb5bd' }} />
        <div className="mt-3">
          <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
            <span className="d-block fw-medium text-dark">Upload a CSV file</span>
            <span className="d-block small text-muted">CSV, up to 250MB</span>
          </label>
          <input
            id="file-upload"
            name="file-upload"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileUpload}
          />
        </div>
      </div>
      {errors.length > 0 && (
        <div className="alert alert-danger d-flex align-items-start gap-2" role="alert">
          <AlertCircle style={{ width: 18, height: 18, marginTop: 4 }} />
          <div>
            <ul className="mb-0 ps-3">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {csvData.length > 0 && (
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h4 className="h6 fw-bold text-dark mb-0">
              Preview ({csvData.length} members)
            </h4>
            <span className={`badge rounded-pill px-2 py-1 ${isValid ? 'bg-success text-white' : 'bg-danger text-white'}`}>{isValid ? "Valid" : "Invalid"}</span>
          </div>
          <div className="border rounded overflow-hidden mb-2">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Group</th>
                  <th>Functions</th>
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 5).map((member, index) => (
                  <tr key={index}>
                    <td className="fw-medium">{member.name}</td>
                    <td>{member.email}</td>
                    <td>{member.groupName}</td>
                    <td>
                      <div>
                        {getFunctionNames(member.functionIds).map((funcName, funcIndex) => (
                          <span key={funcIndex} className="badge bg-secondary me-1 mb-1">
                            {funcName}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {csvData.length > 5 && (
              <div className="p-3 text-center text-muted small">
                ... and {csvData.length - 5} more members
              </div>
            )}
          </div>
          <Button onClick={handleSubmit} disabled={!isValid || isSubmitting} className="btn btn-primary w-100">
            {isSubmitting ? 'Uploading...' : `Send ${csvData.length} Invitation${csvData.length > 1 ? 's' : ''}`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BulkUpload;
